import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { DefaultError } from "@/components/handle-query";
import { Skeleton } from "@/components/skeleton";
import { useAffiliateEarningsQuery } from "@/contexts/queries/affiliate-earnings";
import { useUserReferralsQuery } from "@/contexts/queries/user-referrals";
import { formatDateTime } from "@/utils/format";
import { getServerTime, useServerTimeDelta } from "@/utils/server-time";

import {
  ClientNameCell,
  PortalTable,
  type PortalTableColumn,
  PrimarySubaccountLine,
  SectionCard,
  StatusTag,
} from "../../components";
import { compactAmount, usdCompact } from "../../format";
import type { ClientNamesController } from "../../use-client-names";

const RECENT_LIMIT = 6;
const NEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

interface SignUpRow {
  account: string;
  code: string;
  signedUpMs: number;
  /** Undefined when the referral has no row in the earnings response. */
  volumeUsd: number | undefined;
  ampsEarned: number | undefined;
  isNew: boolean;
}

export const RecentSignUpsCard = ({
  account,
  earningsQuery,
  clientNames,
}: {
  account: string | undefined;
  earningsQuery: ReturnType<typeof useAffiliateEarningsQuery>;
  /** From the page, like the queries: the hook behind it needs the wallet context. */
  clientNames: ClientNamesController;
}) => {
  const t = useTranslations("affiliates.overview.recentSignUps");
  const tStatus = useTranslations("affiliates.status");
  const referralsQuery = useUserReferralsQuery(account);

  // The NEW badge is relative to now, so the card must re-render once the real
  // clock delta arrives.
  useServerTimeDelta();
  const nowMs = getServerTime();

  if (referralsQuery.isError || earningsQuery.isError) {
    return (
      <SectionCard label={t("label")}>
        <DefaultError
          error={referralsQuery.error ?? earningsQuery.error}
          resetErrorBoundary={() => {
            if (referralsQuery.isError) void referralsQuery.refetch();
            if (earningsQuery.isError) void earningsQuery.refetch();
          }}
        />
      </SectionCard>
    );
  }

  if (referralsQuery.data === undefined || earningsQuery.data === undefined) {
    return (
      <SectionCard label={t("label")}>
        <Skeleton className={css({ w: "full", h: "[200px]", rounded: "200" })} />
      </SectionCard>
    );
  }

  // Addresses cross two endpoints; key on lowercase so a casing difference can
  // never silently drop the volume/amps join.
  const earningsByAccount = new Map(
    earningsQuery.data.users.items.map((user) => [user.account.toLowerCase(), user]),
  );

  // `/referrals/users` is already ORDER BY referred_at_ms DESC (handle_referrals.rs:129).
  const rows: SignUpRow[] = referralsQuery.data.slice(0, RECENT_LIMIT).map((referral) => {
    const earnings = earningsByAccount.get(referral.account.toLowerCase());
    return {
      account: referral.account,
      code: referral.referral_code,
      signedUpMs: referral.referred_at_ms,
      volumeUsd: earnings?.total_volume,
      ampsEarned: earnings?.affiliate_amps_earned,
      isNew: nowMs - referral.referred_at_ms < NEW_WINDOW_MS,
    };
  });

  return (
    <SectionCard label={t("label")}>
      <div className={stack({ gap: "12" })}>
        <p className={css({ textStyle: "body.md", color: "text.secondary" })}>{t("description")}</p>
        <PortalTable
          columns={signUpColumns(t, tStatus, clientNames)}
          // Spread into anonymous objects: interfaces lack the implicit index
          // signature PortalTable's row type requires.
          rows={rows.map((row) => ({ ...row }))}
          emptyText={t("empty")}
        />
      </div>
    </SectionCard>
  );
};

type SignUpsTranslator = ReturnType<typeof useTranslations<"affiliates.overview.recentSignUps">>;
type StatusTranslator = ReturnType<typeof useTranslations<"affiliates.status">>;

function signUpColumns(
  t: SignUpsTranslator,
  tStatus: StatusTranslator,
  clientNames: ClientNamesController,
): PortalTableColumn[] {
  return [
    {
      key: "account",
      header: t("columns.user"),
      // The newest referral is the first place an affiliate knows who the address is, so
      // this is where naming it matters most. Copying the address comes with the cell —
      // this column was plain text before.
      render: (row) => {
        const account = String(row.account);
        return (
          <div className={stack({ gap: "2" })}>
            <ClientNameCell
              address={account}
              name={clientNames.nameFor(account)}
              atCapacity={clientNames.atCapacity}
              onSave={(name) => void clientNames.setName(account, name)}
              onRemove={() => clientNames.removeName(account)}
            />
            <PrimarySubaccountLine owner={account} />
          </div>
        );
      },
    },
    { key: "code", header: t("columns.code") },
    {
      key: "signedUpMs",
      header: t("columns.signedUp"),
      render: (row) => formatDateTime(Number(row.signedUpMs), { month: "short", day: "numeric" }),
    },
    {
      key: "volumeUsd",
      header: t("columns.volume"),
      align: "right",
      render: (row) => (row.volumeUsd == null ? "—" : usdCompact(Number(row.volumeUsd))),
    },
    {
      key: "ampsEarned",
      header: t("columns.amps"),
      align: "right",
      render: (row) => (row.ampsEarned == null ? "—" : compactAmount(Number(row.ampsEarned))),
    },
    {
      key: "status",
      header: t("columns.status"),
      align: "right",
      render: (row) => {
        // NEW moved off lime when ACTIVE took it: two lime pills in one column would be
        // separated by their wording alone. Amber measures ΔE 8.2 (deutan) / 17.3 (normal)
        // against lime, where gold would have been 1.7 / 9.7.
        if (row.isNew) return <StatusTag variant="amber" label={tStatus("new")} />;
        // `active` on the earnings DTO is literally `total_volume > 0` — it means
        // "has ever traded", not "traded recently" (handle_affiliate_earnings.rs:192).
        return Number(row.volumeUsd ?? 0) > 0 ? (
          <StatusTag variant="lime" label={tStatus("active")} />
        ) : (
          <StatusTag variant="neutral" label={tStatus("inactive")} />
        );
      },
    },
  ];
}
